import axios from "axios";
import { useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import { Preloader } from "../elements/Preloader";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import Modal, { ModalContent } from "../elements/Modal";
import Button from "../elements/Button";
import { TbArrowsExchange } from "react-icons/tb";

type ChangePasswordProps = {
  setShowChangePassword: (show: boolean) => void;
  /** Sales / account-officer dashboard: PUT /account-officer/change-password */
  accountOfficer?: boolean;
};

const ChangePassword = ({
  setShowChangePassword,
  accountOfficer = false,
}: ChangePasswordProps) => {
  const [errors, setErrors] = useState<any>({});
  const { token } = useAppSelector((store: RootState) => store.auth);
  const [inputValue, setInputValue] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    let isValid = true;
    const newErrors: any = {};

    if (inputValue.currentPassword.length === 0) {
      newErrors.currentPassword = "Please enter your current password";
      isValid = false;
    }

    if (inputValue.newPassword.length === 0) {
      newErrors.newPassword = "Please enter your new password";
      isValid = false;
    }

    if (inputValue.confirmPassword.length === 0) {
      newErrors.confirmPassword = "Please confirm new password";
      isValid = false;
    }

    if (inputValue.newPassword !== inputValue.confirmPassword) {
      newErrors.unmatch = "The new password does not match";
      isValid = false;
    }

    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(inputValue.newPassword)) {
      newErrors.newPassword =
        "Password must be at least 8 characters and contain both letters and numbers";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const authHeader =
    token && (token.startsWith("Bearer ") ? token : `Bearer ${token}`);

  const sendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !process.env.REACT_APP_API_URL) return;
    setIsLoading(true);
    try {
      if (accountOfficer) {
        const res = await axios.put(
          `${process.env.REACT_APP_API_URL}/account-officer/change-password`,
          {
            currentPassword: inputValue.currentPassword,
            newPassword: inputValue.newPassword,
            confirmNewPassword: inputValue.confirmPassword,
          },
          {
            headers: {
              ...(authHeader ? { Authorization: authHeader } : {}),
              "Content-Type": "application/json",
            },
          },
        );
        toast.success(res.data?.message ?? "Password changed successfully");
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/change-password`,
          inputValue,
          {
            headers: {
              ...(authHeader ? { Authorization: authHeader } : {}),
              "Content-Type": "application/json",
            },
          },
        );
        toast.success(res.data?.message ?? "Changed successfully");
      }
      setShowChangePassword(false);
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof (error.response.data as { message?: string }).message === "string"
          ? (error.response.data as { message: string }).message
          : axios.isAxiosError(error) && error.message
            ? error.message
            : "Failed to change password";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={true}
      onClose={() => setShowChangePassword(false)}
      className="flex items-center justify-center"
    >
      <ModalContent className="mx-3 p-6 rounded-md shadow-lg bg-white w-[90%] md:w-[55%] lg:w-[50%] xl:w-[35%]">
        <div className="bg-[#E7F6EF] flex items-center justify-center text-[#008751] h-16 w-16 rounded-[50%] mx-auto">
          <TbArrowsExchange className="w-8 h-8" />
        </div>
        <h1 className="font-semibold text-center pt-6 text-lg">
          Change Password
        </h1>

        <form className="mt-6 flex flex-col gap-4" onSubmit={sendMessage}>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="currentPassword">
              Current Password
            </label>
            <input
              type="password"
              name="currentPassword"
              id="currentPassword"
              className="border border-secondary p-3 rounded-md w-full"
              value={inputValue.currentPassword}
              onChange={(e) =>
                setInputValue((prev) => ({
                  ...prev,
                  currentPassword: e.target.value,
                }))
              }
            />
            {errors.currentPassword && (
              <p className="text-red-500 text-xs">{errors.currentPassword}</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="newPassword">
              New Password
            </label>
            <input
              type="password"
              name="newPassword"
              id="newPassword"
              className="border border-secondary p-3 rounded-md w-full"
              value={inputValue.newPassword}
              onChange={(e) =>
                setInputValue((prev) => ({
                  ...prev,
                  newPassword: e.target.value,
                }))
              }
            />
            {errors.newPassword && (
              <p className="text-red-500 text-xs">{errors.newPassword}</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              className="border border-secondary p-3 rounded-md w-full"
              value={inputValue.confirmPassword}
              onChange={(e) =>
                setInputValue((prev) => ({
                  ...prev,
                  confirmPassword: e.target.value,
                }))
              }
            />
            {errors.unmatch && (
              <p className="text-red-500 text-xs">{errors.unmatch}</p>
            )}
          </div>

          <div className="flex items-center justify-between w-full pt-2">
            <Button
              type="button"
              onClick={() => setShowChangePassword(false)}
              className="border text-gray-500 hover:text-black hover:border-black py-3 px-8"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-secondary text-white py-3 px-8 hover:opacity-90"
            >
              {isLoading ? <Preloader /> : "Save"}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ChangePassword;
